import type {
  DirectCallAudioEvent,
  DirectCallAudioSource,
} from './direct-call-stream';
import type { DirectCallSpeech } from './direct-call-playback';

export interface SpeechStart {
  streamId: string;
  generation: string;
}
export interface SpeechStatus {
  state: string;
  totalChunks: number;
  generation: string;
}
export interface ConversationSpeechApi {
  startSpeech(
    reply: DirectCallSpeech,
    bindingId: string,
    signal: AbortSignal
  ): Promise<SpeechStart>;
  ackSpeech(
    conversationId: string,
    stream: SpeechStart,
    bindingId: string,
    consumedSeq: number,
    signal: AbortSignal
  ): Promise<void>;
  cancelSpeech(
    conversationId: string,
    stream: SpeechStart,
    bindingId: string,
    signal: AbortSignal
  ): Promise<void>;
  speechStatus(
    conversationId: string,
    stream: SpeechStart,
    signal: AbortSignal
  ): Promise<SpeechStatus>;
  replaySpeech(
    conversationId: string,
    stream: SpeechStart,
    bindingId: string,
    fromSeq: number,
    signal: AbortSignal
  ): Promise<unknown[]>;
}
export interface SpeechNotifications {
  readonly bindingId: string | null;
  waitForBinding?(signal: AbortSignal): Promise<string>;
  subscribeSpeech(receive: (type: string, value: unknown) => void): () => void;
}

export class ConversationSpeechSource implements DirectCallAudioSource {
  constructor(
    private readonly api: ConversationSpeechApi,
    private readonly hub: SpeechNotifications,
    private readonly decodeBase64: (value: string) => Uint8Array
  ) {}

  async *stream(
    reply: DirectCallSpeech,
    parentSignal: AbortSignal
  ): AsyncIterable<DirectCallAudioEvent> {
    const binding = this.hub.waitForBinding
      ? await this.hub.waitForBinding(parentSignal)
      : this.hub.bindingId;
    if (
      !binding ||
      !reply.conversationId ||
      !Number.isSafeInteger(reply.eventTurnId) ||
      !Number.isSafeInteger(reply.humanTurnId)
    )
      throw new Error('Speech connection is not ready');
    const operation = new AbortController();
    const signal = operation.signal;
    const cancelOperation = () => operation.abort();
    parentSignal.addEventListener('abort', cancelOperation, { once: true });
    if (parentSignal.aborted) operation.abort();
    const lifetime = setTimeout(cancelOperation, 120000);
    const frames: { type: string; value: Record<string, unknown> }[] = [];
    let wake = () => undefined as void;
    let overflow = false,
      done = false;
    let stream: SpeechStart | undefined;
    const abort = () => wake();
    signal.addEventListener('abort', abort, { once: true });
    let stop = () => undefined as void;
    const receive = (type: string, value: unknown) => {
      if (!value || typeof value !== 'object') return;
      const frame = value as Record<string, unknown>;
      if (
        stream &&
        (frame.streamId !== stream.streamId ||
          frame.generation !== stream.generation)
      )
        return;
      if (frames.length >= 8) overflow = true;
      else frames.push({ type, value: frame });
      wake();
    };
    let next = 0;
    let played = -1;
    let acknowledged = -1;
    let acknowledgment: Promise<void> | undefined;
    let acknowledgmentError: unknown;
    // Coalesce completed buffers while one HTTP ACK is in flight. Scheduling
    // already-received PCM never waits for that request's round trip.
    const acknowledge = () => {
      if (acknowledgment || signal.aborted) return;
      acknowledgment = (async () => {
        while (acknowledged < played && !signal.aborted) {
          const sequence = played;
          await this.api.ackSpeech(
            reply.conversationId!,
            stream!,
            binding,
            sequence,
            signal
          );
          acknowledged = sequence;
        }
      })()
        .catch((error) => {
          acknowledgmentError = error;
          cancelOperation();
        })
        .finally(() => {
          acknowledgment = undefined;
          wake();
          if (acknowledged < played && !signal.aborted) acknowledge();
        });
    };
    let total: number | null = null;
    const chunks = new Map<number, Uint8Array>();
    try {
      stop = this.hub.subscribeSpeech(receive);
      if (signal.aborted) throw new Error('Speech cancelled');
      stream = await this.api.startSpeech(reply, binding, signal);
      if (!stream?.streamId || !stream.generation)
        throw new Error('Invalid speech acknowledgment');
      while (true) {
        if (acknowledgmentError) throw acknowledgmentError;
        if (signal.aborted || this.hub.bindingId !== binding)
          throw new Error('Speech connection changed');
        if (overflow) throw new Error('Speech window exceeded');
        for (const frame of frames.splice(0)) {
          const value = frame.value;
          if (
            value.streamId !== stream.streamId ||
            value.generation !== stream.generation
          )
            continue;
          if (frame.type === 'speechError' || frame.type === 'speechAbort')
            throw new Error('Speech stopped by server');
          if (frame.type === 'speechEnd') {
            if (
              !Number.isSafeInteger(value.totalChunks) ||
              Number(value.totalChunks) < next ||
              Number(value.totalChunks) < 1
            )
              throw new Error('Invalid speech completion');
            total = Number(value.totalChunks);
          } else if (frame.type === 'speechChunk') {
            if (
              value.conversationId !== reply.conversationId ||
              value.messageId !== reply.messageId ||
              value.humanTurnId !== reply.humanTurnId ||
              value.turnId !== reply.eventTurnId ||
              !Number.isSafeInteger(value.seq) ||
              typeof value.pcmBase64 !== 'string' ||
              value.pcmBase64.length > 43692
            )
              throw new Error('Invalid speech attribution');
            const seq = Number(value.seq);
            if (seq < 0 || seq > played + 4)
              throw new Error('Speech sequence outside window');
            if (seq < next || chunks.has(seq)) continue;
            const bytes = this.decodeBase64(value.pcmBase64);
            if (!bytes.length || bytes.length > 32768 || bytes.length % 2)
              throw new Error('Invalid PCM chunk');
            chunks.set(seq, bytes);
          }
        }
        if (total !== null && [...chunks.keys()].some((seq) => seq >= total!))
          throw new Error('Audio after completion');
        if (chunks.has(next)) {
          const bytes = chunks.get(next)!;
          chunks.delete(next);
          const sequence = next++;
          yield {
            type: 'pcm',
            bytes,
            played: () => {
              if (signal.aborted || sequence <= played) return;
              if (sequence !== played + 1 || this.hub.bindingId !== binding) {
                acknowledgmentError = new Error('Invalid playback completion');
                cancelOperation();
                return;
              }
              played = sequence;
              acknowledge();
              wake();
            },
          };
          continue;
        }
        if (total === next && acknowledged === next - 1) {
          done = true;
          yield { type: 'completed' };
          return;
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, 1000);
          wake = () => {
            clearTimeout(timer);
            resolve();
          };
        });
        if (acknowledgmentError) throw acknowledgmentError;
        if (signal.aborted) throw new Error('Speech cancelled');
        if (total === next) continue;
        if (!frames.length) {
          const status = await this.api.speechStatus(
            reply.conversationId,
            stream,
            signal
          );
          if (
            status.generation !== stream.generation ||
            status.state === 'failed' ||
            status.state === 'cancelled'
          )
            throw new Error('Speech stream unavailable');
          if (status.state === 'completed') {
            if (
              !Number.isSafeInteger(status.totalChunks) ||
              status.totalChunks < next ||
              status.totalChunks < 1
            )
              throw new Error('Invalid speech status');
            total = status.totalChunks;
          }
          if (
            (status.state === 'streaming' || status.state === 'completed') &&
            total !== next
          ) {
            const replay = await this.api.replaySpeech(
              reply.conversationId,
              stream,
              binding,
              next,
              signal
            );
            if (!Array.isArray(replay) || replay.length > 4)
              throw new Error('Invalid speech replay');
            for (const frame of replay) receive('speechChunk', frame);
            if (
              status.state === 'completed' &&
              !replay.length &&
              !chunks.has(next)
            )
              throw new Error('Speech replay unavailable');
          }
        }
      }
    } finally {
      clearTimeout(lifetime);
      parentSignal.removeEventListener('abort', cancelOperation);
      operation.abort();
      stop();
      signal.removeEventListener('abort', abort);
      chunks.clear();
      if (stream && !done) {
        const cancel = new AbortController();
        const timer = setTimeout(() => cancel.abort(), 2000);
        void this.api
          .cancelSpeech(reply.conversationId, stream, binding, cancel.signal)
          .catch(() => undefined)
          .finally(() => clearTimeout(timer));
      }
    }
  }
}
